import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Form, Input, Checkbox } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { FlexRowBetween, TitleText } from 'components'
import { AppPrimaryButton, AppButton } from 'elements'
import { X } from 'phosphor-react'
import {
    AutoEmailToggle,
    CompactToggleCard,
    FieldRow,
    FormCard,
    FormCardBody,
    FormCardHead,
    ModalContentWrap,
    ModalShell,
    ModalTopBar,
    PreviewBody,
    PreviewCard,
    PreviewHead,
    PreviewSection,
    PreviewSummary,
    RecipientRow,
} from './notification-group-styles'
import {
    buildRecipientPreview,
    getInitials,
    parseGroupToForm,
} from './notification-group-utils'

const toArray = value => (Array.isArray(value) ? value : [])

export const NotificationGroupModal = ({
    open,
    onClose,
    onSubmit,
    loading,
    initialGroup,
    apiError,
    templateOptions = [],
    customerOptions = [],
    userOptions = [],
    usersData = [],
    customersData = [],
    templatesData = [],
}) => {
    const [groupName, setGroupName] = useState('')
    const [autoEmail, setAutoEmail] = useState(true)
    const [onHold, setOnHold] = useState(false)
    const [templateIds, setTemplateIds] = useState([])
    const [customerIds, setCustomerIds] = useState([])
    const [userIds, setUserIds] = useState([])
    const [otherEmails, setOtherEmails] = useState([])
    const [emailInput, setEmailInput] = useState('')
    const [validationError, setValidationError] = useState('')
    const [templatesOpen, setTemplatesOpen] = useState(false)
    const [customersOpen, setCustomersOpen] = useState(false)
    const [usersOpen, setUsersOpen] = useState(false)

    const isEdit = Boolean(initialGroup?.GroupId)

    const closeAllDropdowns = () => {
        setTemplatesOpen(false)
        setCustomersOpen(false)
        setUsersOpen(false)
    }

    useEffect(() => {
        if (!open) return
        const form = parseGroupToForm(initialGroup)
        setGroupName(form.groupName)
        setAutoEmail(form.autoEmail)
        setOnHold(form.onHold)
        setTemplateIds(form.templateIds)
        setCustomerIds(form.customerIds)
        setUserIds(form.userIds)
        setOtherEmails(form.otherEmails)
        setEmailInput('')
        setValidationError('')
        closeAllDropdowns()
    }, [open, initialGroup])

    const preview = useMemo(
        () =>
            buildRecipientPreview(
                { userIds, customerIds, templateIds, otherEmails },
                toArray(usersData),
                toArray(customersData),
                toArray(templatesData),
            ),
        [
            userIds,
            customerIds,
            templateIds,
            otherEmails,
            usersData,
            customersData,
            templatesData,
        ],
    )

    const triggerText = useMemo(() => {
        if (!preview.templates.length) {
            return 'Select at least one template to see the trigger summary.'
        }
        const names = preview.templates.map(t => t.TemplateName).join(' or ')
        if (preview.customers.length === 1) {
            return `When ${names} is completed for customer ${preview.customers[0].customer_name}.`
        }
        if (preview.customers.length > 1) {
            const customerNames = preview.customers
                .map(c => c.customer_name)
                .join(', ')
            return `When ${names} is completed for customers ${customerNames}.`
        }
        return `When ${names} is completed for any customer.`
    }, [preview.templates, preview.customers])

    const handleAddEmail = () => {
        const email = emailInput.trim()
        if (!email) return
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            setValidationError('Enter a valid email address.')
            return
        }
        if (otherEmails.includes(email)) {
            setValidationError('This email is already in the list.')
            return
        }
        setOtherEmails([...otherEmails, email])
        setEmailInput('')
        setValidationError('')
    }

    const handleRemoveEmail = email => {
        setOtherEmails(otherEmails.filter(e => e !== email))
    }

    const handleSave = () => {
        const trimmedName = groupName.trim()
        if (!trimmedName) {
            setValidationError('Group name is required.')
            return
        }
        if (!templateIds.length) {
            setValidationError('Select at least one template.')
            return
        }

        setValidationError('')
        onSubmit({
            groupId: initialGroup?.GroupId || null,
            groupName: trimmedName,
            autoEmail,
            onHold,
            templateIds,
            customerIds,
            userIds,
            otherEmails,
        })
    }

    const handleClose = () => {
        setValidationError('')
        onClose()
    }

    return (
        <Modal open={open} onClose={handleClose} size="large" className="model">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>
                        {isEdit
                            ? 'Edit Notification Group'
                            : 'Create Notification Group'}
                    </TitleText>
                    <X className="pointer" size={32} onClick={handleClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content style={{ background: '#f1f5f9' }}>
                <ModalContentWrap>
                    <ModalTopBar>
                        <CompactToggleCard $off={!onHold}>
                            <Checkbox
                                toggle
                                checked={onHold}
                                onChange={(_, { checked }) =>
                                    setOnHold(Boolean(checked))
                                }
                            />
                            <div className="toggle-copy">
                                <div className="toggle-label">
                                    Hold send notification
                                </div>
                                <div className="toggle-desc">
                                    Pause notifications for this group.
                                </div>
                            </div>
                        </CompactToggleCard>
                    </ModalTopBar>
                    <ModalShell>
                    <FormCard>
                        <FormCardHead>
                            {isEdit
                                ? 'Edit notification group'
                                : 'Create notification group'}
                        </FormCardHead>
                        <FormCardBody>
                            <Form>
                                <FieldRow>
                                    <label>
                                        Group name <span className="req">*</span>
                                    </label>
                                    <Input
                                        fluid
                                        placeholder="e.g. Daily Inspection Auto-Notify"
                                        value={groupName}
                                        onChange={(_, { value }) =>
                                            setGroupName(value)
                                        }
                                    />
                                    <span className="hint">
                                        A label admins recognize. Used only for
                                        the list view.
                                    </span>
                                </FieldRow>

                                {isEdit ? (
                                    <FieldRow>
                                        <AutoEmailToggle
                                            $off={!autoEmail}
                                            role="presentation"
                                        >
                                            <Checkbox
                                                toggle
                                                checked={autoEmail}
                                                onChange={(_, { checked }) =>
                                                    setAutoEmail(checked)
                                                }
                                            />
                                            <div>
                                                <div className="toggle-label">
                                                    Auto-send email when an
                                                    assigned template is
                                                    completed
                                                </div>
                                                <div className="toggle-desc">
                                                    Turn off if this group is only
                                                    used for manual sharing later.
                                                </div>
                                            </div>
                                        </AutoEmailToggle>
                                    </FieldRow>
                                ) : (
                                    <FieldRow>
                                        <div
                                            style={{
                                                padding: '10px 12px',
                                                background: '#ecfdf5',
                                                border: '1px solid #a7f3d0',
                                                borderRadius: 8,
                                                fontSize: 12.5,
                                                color: '#065f46',
                                            }}
                                        >
                                            <strong>Auto-email:</strong> New
                                            groups are created with auto-email{' '}
                                            <strong>ON</strong> (set by the
                                            server). To turn it off, save this
                                            group first, then use{' '}
                                            <strong>Edit</strong>.
                                        </div>
                                    </FieldRow>
                                )}

                                <FieldRow>
                                    <label>
                                        Templates this group covers{' '}
                                        <span className="req">*</span>
                                    </label>
                                    <Dropdown
                                        fluid
                                        multiple
                                        search
                                        selection
                                        closeOnChange
                                        placeholder="Select templates"
                                        options={templateOptions}
                                        value={templateIds}
                                        open={templatesOpen}
                                        onOpen={() => {
                                            closeAllDropdowns()
                                            setTemplatesOpen(true)
                                        }}
                                        onClose={() => setTemplatesOpen(false)}
                                        onChange={(_, { value }) =>
                                            setTemplateIds(value)
                                        }
                                    />
                                    <span className="hint">
                                        Email fires when any of these completes
                                        (and the audit&apos;s customer matches
                                        below, if set).
                                    </span>
                                </FieldRow>

                                <FieldRow>
                                    <label>Customer scope (optional)</label>
                                    <Dropdown
                                        fluid
                                        multiple
                                        search
                                        selection
                                        closeOnChange
                                        placeholder="All customers (leave empty)"
                                        options={customerOptions}
                                        value={customerIds}
                                        open={customersOpen}
                                        onOpen={() => {
                                            closeAllDropdowns()
                                            setCustomersOpen(true)
                                        }}
                                        onClose={() => setCustomersOpen(false)}
                                        onChange={(_, { value }) =>
                                            setCustomerIds(value)
                                        }
                                    />
                                    <span className="hint">
                                        Leave empty — email fires for any
                                        customer. Add customers — only fires when
                                        the audit&apos;s customer matches.
                                    </span>
                                </FieldRow>

                                <FieldRow>
                                    <label>Recipients — internal users</label>
                                    <Dropdown
                                        fluid
                                        multiple
                                        search
                                        selection
                                        closeOnChange
                                        placeholder="Select team members"
                                        options={userOptions}
                                        value={userIds}
                                        open={usersOpen}
                                        onOpen={() => {
                                            closeAllDropdowns()
                                            setUsersOpen(true)
                                        }}
                                        onClose={() => setUsersOpen(false)}
                                        onChange={(_, { value }) =>
                                            setUserIds(value)
                                        }
                                    />
                                    <span className="hint">
                                        Emails resolve from the user database at
                                        send time.
                                    </span>
                                </FieldRow>

                                <FieldRow>
                                    <label>
                                        Recipients — extra external emails
                                    </label>
                                    <Input
                                        fluid
                                        placeholder="Enter email address"
                                        value={emailInput}
                                        onFocus={closeAllDropdowns}
                                        onChange={(_, { value }) =>
                                            setEmailInput(value)
                                        }
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                handleAddEmail()
                                            }
                                        }}
                                        action={{
                                            content: 'Add',
                                            onClick: handleAddEmail,
                                        }}
                                    />
                                    {otherEmails.length > 0 && (
                                        <div
                                            style={{
                                                marginTop: 8,
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 6,
                                            }}
                                        >
                                            {otherEmails.map(email => (
                                                <span
                                                    key={email}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        padding: '3px 9px',
                                                        background: '#fef3c7',
                                                        color: '#92400e',
                                                        borderRadius: 999,
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {email}
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleRemoveEmail(
                                                                email,
                                                            )
                                                        }
                                                        style={{
                                                            border: 'none',
                                                            background: 'none',
                                                            cursor: 'pointer',
                                                            padding: 0,
                                                            fontWeight: 700,
                                                            color: '#92400e',
                                                        }}
                                                        aria-label={`Remove ${email}`}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <span className="hint">
                                        For people without an eAuditor account.
                                        Comma- or semicolon-separated input is
                                        parsed on save.
                                    </span>
                                </FieldRow>
                            </Form>
                        </FormCardBody>
                    </FormCard>

                    <PreviewCard>
                        <PreviewHead>Who will get this email?</PreviewHead>
                        <PreviewBody>
                            <PreviewSection>
                                <h4>Trigger</h4>
                                <div
                                    style={{
                                        fontSize: 12.5,
                                        color: '#334155',
                                    }}
                                >
                                    {triggerText}
                                </div>
                            </PreviewSection>

                            <PreviewSection>
                                <h4>
                                    Internal users ({preview.users.length})
                                </h4>
                                {preview.users.length === 0 ? (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: '#64748b',
                                        }}
                                    >
                                        None selected
                                    </div>
                                ) : (
                                    preview.users.map(user => (
                                        <RecipientRow key={user.unique_id}>
                                            <div className="avatar">
                                                {getInitials(user.Name)}
                                            </div>
                                            <div className="who">
                                                <div className="name">
                                                    {user.Name}
                                                </div>
                                                <div className="mail">
                                                    {user.Email}
                                                </div>
                                            </div>
                                            <span className="badge">
                                                Internal
                                            </span>
                                        </RecipientRow>
                                    ))
                                )}
                            </PreviewSection>

                            <PreviewSection>
                                <h4>
                                    External emails ({preview.externals.length})
                                </h4>
                                {preview.externals.length === 0 ? (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: '#64748b',
                                        }}
                                    >
                                        None added
                                    </div>
                                ) : (
                                    preview.externals.map(email => (
                                        <RecipientRow key={email}>
                                            <div className="avatar">@</div>
                                            <div className="who">
                                                <div className="name">
                                                    {email}
                                                </div>
                                                <div className="mail">
                                                    No account
                                                </div>
                                            </div>
                                            <span
                                                className="badge"
                                                $variant="external"
                                            >
                                                External
                                            </span>
                                        </RecipientRow>
                                    ))
                                )}
                            </PreviewSection>

                            {preview.customers.length > 0 && (
                                <PreviewSection>
                                    <h4>
                                        Customer scope (
                                        {preview.customers.length})
                                    </h4>
                                    {preview.customers.map(customer => (
                                        <RecipientRow
                                            key={customer.unique_id}
                                        >
                                            <div className="avatar">C</div>
                                            <div className="who">
                                                <div className="name">
                                                    {customer.customer_name}
                                                </div>
                                                <div className="mail">
                                                    Restricts to this customer
                                                </div>
                                            </div>
                                            <span
                                                className="badge"
                                                $variant="customer"
                                            >
                                                Customer
                                            </span>
                                        </RecipientRow>
                                    ))}
                                </PreviewSection>
                            )}

                            <PreviewSummary>
                                {preview.total > 0
                                    ? `✓ ${preview.total} direct recipient${preview.total === 1 ? '' : 's'} · sender excluded`
                                    : 'Add internal users or external emails to receive notifications.'}
                            </PreviewSummary>
                        </PreviewBody>
                    </PreviewCard>
                </ModalShell>
                </ModalContentWrap>

                {validationError && (
                    <div
                        className="ui negative message"
                        style={{ marginTop: 16 }}
                    >
                        {validationError}
                    </div>
                )}
                {apiError && (
                    <div
                        className="ui negative message"
                        style={{ marginTop: 16 }}
                    >
                        {apiError}
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                <AppButton type="button" onClick={handleClose} disabled={loading}>
                    Cancel
                </AppButton>
                <AppPrimaryButton
                    type="button"
                    onClick={handleSave}
                    loading={loading}
                    disabled={loading}
                >
                    Save group
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}
